// ============================================================================
// image-text-reading.ts: the one implementation of "read the text in an image"
// ----------------------------------------------------------------------------
// Peter, 2026-10-13: "Make sure to process jpg and image files that may be the
// RFQ ... Make it so QuoteMaker can do it too for References files that are
// images but have text."
//
// Used by:
//   - Stage 1 import (Stage1DataEntry handleDocsFiles, and the image routing in
//     stage1-image-text-routing.ts), so an RFQ photo, a scan or a screenshot
//     reaches RFQ extraction as a document;
//   - the Reference Files viewer's "Read text" action (ReferenceFilesPanel).
//
// The model comes from the registry ('image-ocr', pickModelForTask). There is
// no timeout and no AbortController on the AI call (CLAUDE.md standing rule).
// Logs carry text LENGTHS only: OCR text holds client names, phones and
// addresses.
// ============================================================================

import { callOpenRouterVision, getMaxOutputTokens } from './ai-api';
import { isVisionCapableModel, pickModelForTask } from './model-selector';
import { getPrompt } from '../config/pipeline-registry';
import { OCR_DOCUMENT_PROMPT } from '../config/prompts';

export type ImageSource = Blob | string;

/** Long side of the image sent for OCR. 2048 keeps a portrait page legible. */
export const OCR_MAX_LONG_SIDE = 2048;
export const OCR_JPEG_QUALITY = 0.85;
/** The OCR prompt's answer when an image carries no significant text. */
export const NO_TEXT_FOUND = 'NO_TEXT_FOUND';
/** A "document" image needs at least this much text to leave the photo lane. */
export const MIN_DOCUMENT_TEXT_CHARS = 80;

export interface RenderedImage {
    dataUrl: string;
    width: number;
    height: number;
    sourceWidth: number;
    sourceHeight: number;
}

export type ImageTextReadStatus = 'text' | 'no-text' | 'skipped' | 'failed';

export interface ImageTextReadResult {
    status: ImageTextReadStatus;
    text: string;
    model?: string;
    reason?: string;
}

/** Scale (width, height) so the long side is at most maxSide. Never enlarges. */
export function fitWithinLongSide(width: number, height: number, maxSide: number): { width: number; height: number } {
    const w = Math.round(Number(width) || 0);
    const h = Math.round(Number(height) || 0);
    if (w <= 0 || h <= 0) return { width: 0, height: 0 };
    const scale = Math.min(1, Number(maxSide) / Math.max(w, h));
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
    };
}

export function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Could not read the image bytes.'));
        reader.readAsDataURL(blob);
    });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob | null> {
    const match = /^data:([^;,]*)((?:;[^;,]*)*),([\s\S]*)$/.exec(dataUrl);
    if (!match) return null;
    const mime = match[1] || 'application/octet-stream';
    const payload = match[3] || '';
    try {
        if (/;base64/i.test(match[2] || '')) {
            const binary = atob(payload);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
            return new Blob([bytes], { type: mime });
        }
        return new Blob([decodeURIComponent(payload)], { type: mime });
    } catch {
        return null;
    }
}

/**
 * Draw an image as a JPEG whose long side is at most maxSide, or null when the
 * browser cannot decode it (HEIC on most desktop browsers, a damaged file).
 *
 * Preferred path: createImageBitmap decodes off the main thread and
 * OffscreenCanvas.convertToBlob encodes asynchronously. The fallback uses an
 * <img> and canvas.toBlob, which is still asynchronous. The old Stage 1 helper
 * used canvas.toDataURL, a synchronous encode that can freeze the tab on a
 * large photo.
 */
export async function renderImageToJpeg(source: ImageSource, maxSide: number, quality: number): Promise<RenderedImage | null> {
    const g = globalThis as any;
    if (typeof g.createImageBitmap === 'function' && typeof g.OffscreenCanvas === 'function') {
        let bitmap: any = null;
        try {
            const blob = typeof source === 'string' ? await dataUrlToBlob(source) : source;
            if (blob) {
                bitmap = await g.createImageBitmap(blob);
                const size = fitWithinLongSide(bitmap.width, bitmap.height, maxSide);
                if (size.width > 0) {
                    const canvas = new g.OffscreenCanvas(size.width, size.height);
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(bitmap, 0, 0, size.width, size.height);
                        const out: Blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
                        return {
                            dataUrl: await blobToDataUrl(out),
                            width: size.width,
                            height: size.height,
                            sourceWidth: bitmap.width,
                            sourceHeight: bitmap.height,
                        };
                    }
                }
            }
        } catch {
            // Not decodable here; the element path below gives it one more go.
        } finally {
            try { bitmap?.close?.(); } catch { /* already released */ }
        }
    }

    if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
    const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
    try {
        const img = await new Promise<HTMLImageElement | null>((resolve) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => resolve(null);
            el.src = objectUrl || (source as string);
        });
        if (!img) return null;
        const sourceWidth = img.naturalWidth || img.width;
        const sourceHeight = img.naturalHeight || img.height;
        const size = fitWithinLongSide(sourceWidth, sourceHeight, maxSide);
        if (size.width <= 0) return null;
        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = canvas.getContext('2d');
        if (!ctx || typeof canvas.toBlob !== 'function') return null;
        ctx.drawImage(img, 0, 0, size.width, size.height);
        const out = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
        if (!out) return null;
        return { dataUrl: await blobToDataUrl(out), width: size.width, height: size.height, sourceWidth, sourceHeight };
    } catch {
        return null;
    } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
}

/**
 * The OCR copy of an image: long side capped at 2048px (the old helper fitted
 * 1600x1200, which cut a portrait page to 1200px tall). An image this browser
 * cannot decode is sent as it is, which is what the old helper did.
 */
export async function compressImageForOCR(source: ImageSource, maxLongSide: number = OCR_MAX_LONG_SIDE): Promise<string> {
    const rendered = await renderImageToJpeg(source, maxLongSide, OCR_JPEG_QUALITY);
    if (rendered) return rendered.dataUrl;
    return typeof source === 'string' ? source : blobToDataUrl(source);
}

/** The model's answer as text, or '' when it reported no text. */
export function normaliseOcrText(raw: unknown): string {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) return '';
    if (/^["'`]*NO_TEXT_FOUND["'`.]*$/i.test(text)) return '';
    return text;
}

/**
 * Read the text in one image with the registry's 'image-ocr' model.
 * Never throws: a failed call comes back as status 'failed' with a reason.
 */
export async function readTextFromImage({
    image,
    fileName = 'image',
    apiKey,
    model = '',
    projectId = null,
    stageName = '1',
}: {
    image: ImageSource;
    fileName?: string;
    apiKey?: string | null;
    model?: string | null;
    projectId?: string | null;
    stageName?: string;
}): Promise<ImageTextReadResult> {
    if (!apiKey) return { status: 'skipped', text: '', reason: 'No AI connection is set up.' };
    const ocrModel = pickModelForTask('image-ocr', model || '');
    if (!isVisionCapableModel(ocrModel)) {
        console.warn('[ImageText] OCR skipped: the image-ocr route is not vision-capable:', ocrModel);
        return { status: 'skipped', text: '', model: ocrModel, reason: 'The text-reading model cannot read images.' };
    }
    const prompt = getPrompt('ocr-document') || OCR_DOCUMENT_PROMPT;
    try {
        const compressed = await compressImageForOCR(image);
        const raw = await callOpenRouterVision({
            apiKey,
            model: ocrModel,
            prompt,
            images: [compressed],
            temperature: 0.1,
            maxTokens: getMaxOutputTokens(ocrModel),
            pipelineId: 'photo-ocr',
            stageName,
            signal: undefined,
            task: 'image-ocr',
            // Same as every Stage 1 vision call: no client transport timer.
            timeoutMs: 0,
            responseFormat: undefined,
            reasoningEffort: undefined,
            projectId: projectId || undefined,
            logicalCallId: undefined,
            supportOperation: undefined,
        });
        const text = normaliseOcrText(raw);
        console.log(`[ImageText] ${fileName}: ${text.length} characters read`);
        return text
            ? { status: 'text', text, model: ocrModel }
            : { status: 'no-text', text: '', model: ocrModel };
    } catch (err: any) {
        const reason = String(err?.message || 'The text could not be read.');
        console.warn(`[ImageText] ${fileName}: text reading failed:`, reason);
        return { status: 'failed', text: '', model: ocrModel, reason };
    }
}

/**
 * The apiKey a component outside the Stage pipeline passes to the AI layer.
 * Mirrors App.tsx's `apiKey` state: a dev/admin browser with qm_dev_keys=1 may
 * hold a session key; everyone else uses the 'gateway' sentinel, which the AI
 * layer never sends anywhere (the backend gateway holds the provider keys).
 */
export function defaultImageTextApiKey(): string {
    try {
        if (localStorage.getItem('qm_dev_keys') === '1') {
            const sessionKey = sessionStorage.getItem('qm_apikey');
            if (sessionKey) return sessionKey;
        }
    } catch { /* storage unavailable */ }
    return 'gateway';
}

/** The bytes of a stored photo or image document, or null when none can be found. */
export async function resolveStoredImageSource(item: any, projectId: string | null = null): Promise<ImageSource | null> {
    if (!item || typeof item !== 'object') return null;
    for (const key of ['data', 'dataUrl']) {
        const value = item[key];
        if (typeof value === 'string' && /^data:image\//i.test(value)) return value;
    }
    if (item.docBlobId) {
        try {
            const { getDocumentBlob } = await import('./document-blob-store');
            const blob = await getDocumentBlob(item.docBlobId, projectId);
            if (blob instanceof Blob && blob.size > 0) return blob;
        } catch { /* fall through to the photo resolver */ }
    }
    try {
        const { resolvePhotoDataUrl } = await import('./photo-auto-assign');
        const url = await resolvePhotoDataUrl(item, projectId);
        if (typeof url === 'string' && url) return url;
    } catch { /* nothing else to try */ }
    return null;
}

/** "Read text" for a photo or image document already on the project. */
export async function readTextFromStoredImage(item: any, {
    projectId = null,
    apiKey = null,
    model = '',
}: { projectId?: string | null; apiKey?: string | null; model?: string | null } = {}): Promise<ImageTextReadResult> {
    const image = await resolveStoredImageSource(item, projectId);
    if (!image) return { status: 'failed', text: '', reason: 'The image file could not be opened.' };
    return readTextFromImage({
        image,
        fileName: String(item?.name || item?.originalName || 'image'),
        apiKey: apiKey || defaultImageTextApiKey(),
        model,
        projectId,
        stageName: 'reference-files',
    });
}

/**
 * Run `worker` over items with at most `limit` running at once, in order.
 * `shouldStop` is checked before each item starts; items not started keep an
 * undefined result.
 */
export async function mapWithConcurrency<T, R>(
    items: readonly T[],
    limit: number,
    worker: (item: T, index: number) => Promise<R>,
    shouldStop: () => boolean = () => false,
): Promise<Array<R | undefined>> {
    const results: Array<R | undefined> = new Array(items.length).fill(undefined);
    let next = 0;
    const laneCount = Math.max(1, Math.min(Math.floor(limit) || 1, items.length));
    const lanes = Array.from({ length: laneCount }, async () => {
        while (next < items.length) {
            if (shouldStop()) return;
            const index = next;
            next += 1;
            results[index] = await worker(items[index] as T, index);
        }
    });
    await Promise.all(lanes);
    return results;
}
